import classNames from 'classnames';

import PaperAirplaneIcon from '@heroicons/react/20/solid/PaperAirplaneIcon';
import { useTheme } from '../../../context/theme/useTheme';
import { Card } from '../../_misc/layouts/Card';
import { LinkAuthenticationElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { useAuth } from '../../../context/auth/useAuth';
import { SimpleFooter } from '../../_misc/layouts/SimpleFooter';

import { handleApiError } from '../../../api/utils';
import toast from 'react-hot-toast';
import { Frequency, Tier } from '../subscriptions.constants';
import { FeatureCategory } from '../FeatureCategory';
import { AxiosError } from 'axios';
import { ThemeBackground } from '../../../context/theme/ThemeConstantsUpdated';
import { LoadingLines } from '../../_misc/loaders/LoadingLines';

const { borderBackground } = ThemeBackground;

export const PurchaseSubscriptionForm = (props: Props) => {
  const { tier, frequency, onCancel, onConfirm, clientSecret, isDisabled } = props;
  const { currentUser } = useAuth();
  const [hasPaymentInfo, setHasPaymentInfo] = useState(false);
  const [isLinkElementReady, setIsLinkElementReady] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const isFormReady = isLinkElementReady && isPaymentElementReady;
  const stripe = useStripe();
  const elements = useElements();
  const price = tier.price[frequency.value];
  const [isSubmitting, setIsSubmitting] = useState<boolean>();

  const handleSubscribe = async () => {
    if (!stripe || !elements) return;

    try {
      setIsSubmitting(true);

      // Get the Payment Element
      const paymentElement = elements.getElement('payment');
      if (!paymentElement) {
        toast.error('Payment element not found');
        setIsSubmitting(false);
        return;
      }

      // Submit the Payment Element to validate input
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || 'Payment submission failed');
        setIsSubmitting(false);
        return;
      }

      const resp = await stripe.confirmPayment({ elements, clientSecret, redirect: 'if_required' });

      if (resp.error) {
        toast.error(resp.error.message || 'Payment failed... Please try again.');
        setIsSubmitting(false);
        return;
      } else {
        toast.success('Subscription successful!');
        await onConfirm();
      }
    } catch (error) {
      handleApiError(error as AxiosError<{ message: string; error: string }>);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto mt-10 max-w-lg space-y-4">
      <Card
        includeShadow
        footer={
          <SimpleFooter
            isSubmitDisabled={!hasPaymentInfo || isSubmitting || isDisabled}
            onSubmit={handleSubscribe}
            submitLabel={`Subscribe ${price}`}
            submitIcon={PaperAirplaneIcon}
            cancelLabel="Cancel"
            onCancel={onCancel}
          />
        }
      >
        <div className="mb-5">
          <div className={classNames(borderBackground, 'mb-4 border-b pb-3')}>
            <FeatureCategory features={tier.features.map((f) => f.label)} />
          </div>

          {!isFormReady && (
            <div className="flex items-center justify-center p-5">
              <LoadingLines />
            </div>
          )}

          <div className={isFormReady ? undefined : 'invisible h-0 overflow-hidden'}>
            <LinkAuthenticationElement
              onReady={() => setIsLinkElementReady(true)}
              onChange={(e) => setHasPaymentInfo(e.complete)}
              options={{ defaultValues: { email: currentUser!.email! } }}
            />

            <PaymentElement
              onReady={() => setIsPaymentElementReady(true)}
              onChange={(e) => setHasPaymentInfo(e.complete)}
              options={{
                defaultValues: {
                  billingDetails: { email: currentUser!.email! },
                },
                layout: 'tabs',
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

type Props = {
  tier: Tier;
  frequency: Frequency;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  clientSecret: string;
  isDisabled: boolean;
};
